import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseIndexPageComponent } from '../../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnActions } from '../../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnText } from '../../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import ApiOptionsConfig from '../../../../services/api/api-options.config';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import {
  CompanyDocument,
  companyDocumentRevisionInProgressId
} from '../../../../models/entities/company-document';

@Component({
  selector: 'app-company-documents-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './company-documents-index-page.component.html',
  styleUrl: './company-documents-index-page.component.scss',
})
export class CompanyDocumentsIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  private activeProfileCode: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    protected qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Company Documents',
      cardTitle: 'Document',
      dataTable: {
        data: [],
        searchModel: {
          code: null,
          department: null,
          name: null,
          companyDocumentGroupId: null,
          allSigned: null,
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            value: (item: CompanyDocument) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'department',
            header: 'Department',
            filter: 'select',
            filterOptionsSource: 'companyDocumentDepartments',
            value: (item: CompanyDocument) => item?.department ?? ''
          }),
          new DataTableColumnText({
            attribute: 'name',
            header: 'Name',
            filter: 'text',
            value: (item: CompanyDocument) => item?.name ?? ''
          }),
          new DataTableColumnText({
            attribute: 'companyDocumentGroupId',
            header: 'Group',
            filter: 'select',
            filterOptionsSource: 'companyDocumentGroups',
            value: (item: CompanyDocument) => item?.companyDocumentGroup?.name || '-'
          }),
          new DataTableColumnText({
            attribute: 'currentRevisionCode',
            header: 'Revision',
            value: (item: CompanyDocument) => item?.currentRevisionCode || '-'
          }),
          new DataTableColumnText({
            attribute: 'allSigned',
            header: 'Signed',
            filter: 'select',
            filterOptionsSource: 'boolean',
            width: '100px',
            value: (item: CompanyDocument) => item?.allSigned == null ? '-' : (item.allSigned ? 'Yes' : 'No')
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '160px' },
            buttons: [
              {
                icon: 'fa-plus',
                tooltip: 'New revision (opens the one in progress, if any)',
                aclAction: 'modify',
                onClick: (item: CompanyDocument) => this.openNewOrInProgressRevision(item),
                hidden: () => !this.canModify()
              },
              {
                icon: 'fa-eye',
                tooltip: 'View companyDocument details',
                onClick: (item: CompanyDocument) => {
                  this.routeService.companyDocumentsDetail(Number(item.companyDocumentId));
                }
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit companyDocument',
                onClick: (item: CompanyDocument) => {
                  this.routeService.companyDocumentsUpdate(Number(item.companyDocumentId));
                },
                hidden: () => !this.canModify()
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete companyDocument',
                onClick: () => { },
                isDelete: true,
                hidden: () => !this.canDelete()
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.qualityManagementService.listCompanyDocuments(options).pipe(
        map((response: any) => ({
          ...response,
          data: {
            companyDocuments: this.qualityManagementService.readResponseKey(response, 'companyDocuments') ?? []
          },
          pagination: response?.pagination ?? response?.data?.pagination ?? null
        }))
      ),
      responseListModelName: 'companyDocuments',
    }
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;

        if (this.canModify()) {
          this.baseIndexPageConfig.addNewItemButton = {
            type: 'success',
            title: 'New document',
            onClick: () => this.routeService.companyDocumentsCreate()
          };
        } else {
          this.baseIndexPageConfig.addNewItemButton = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private openNewOrInProgressRevision(item: CompanyDocument): void {
    const companyDocumentId = Number(item?.companyDocumentId);
    if (!companyDocumentId) {
      return;
    }

    if (item.revisionInProgress !== undefined) {
      this.navigateToRevision(companyDocumentId, companyDocumentRevisionInProgressId(item));
      return;
    }

    this.qualityManagementService.detailCompanyDocument(companyDocumentId).subscribe({
      next: (response: any) => {
        const companyDocument: CompanyDocument | null =
          this.qualityManagementService.readResponseKey(response, 'companyDocument') ?? null;
        this.navigateToRevision(companyDocumentId, companyDocumentRevisionInProgressId(companyDocument));
      },
      error: (error: any) => {
        console.error('Error loading the company document:', error);
        this.alertNotificationService.error(
          this.qualityManagementService.companyDocumentErrorMessage(error, 'Failed to load the company document.')
        );
      }
    });
  }

  private navigateToRevision(companyDocumentId: number, revisionInProgressId: number | null): void {
    if (revisionInProgressId) {
      this.routeService.companyDocumentsRevisionDetail(revisionInProgressId);
      return;
    }
    this.routeService.companyDocumentsRevisionCreate(companyDocumentId);
  }

  private canModify(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
      'modify'
    );
  }

  private canDelete(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_COMPANY_DOCUMENTS',
      'delete'
    );
  }
}
